import { memo, useCallback, useMemo, useState } from "react";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import PopupTest from "../components/PopUpTest";
import DeletePopUp from "../components/DeletePopUp";
import AppShell from "../components/AppShell";
import MobileHeader from "../components/MobileHeader";
import PackageAssignFlow from "../components/PackageAssignFlow";
import PackageDetailsModal from "../components/PackageDetailsModal";
import ClientPackageDialog from "../components/ClientPackageDialog";
import MembersSortControl, {
  compareMembersBy,
} from "../components/MembersSortControl";
import { useAlert } from "../../../context/AlertContext";
import {
  useMembers,
  refreshMembers,
  upsertClientInCache,
  removeClientFromCache,
  upsertClientPackageInCache,
} from "../../../data/membersStore";
import "./members.css";

const AVATAR_COLORS = [
  "#2563EB",
  "#10B981",
  "#F59E0B",
  "#6366F1",
  "#EC4899",
  "#14B8A6",
];

// "Predhodne termine" is a real `packages.name` value in the DB — the legacy-backfill
// placeholder package created for clients whose historical bookings were migrated in without a
// real purchased package (0 sessions, no real price). Showing that literal name as if it were
// an actual package reads as confusing/meaningless to the trainer, so it's relabeled for
// display only here — the underlying package row/data is untouched.
const displayPackageName = (name) =>
  name === "Predhodne termine" ? "Nema aktivnih paketa" : name;

const colorForName = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Extracted + memoized so typing in the search box (which re-renders
// Members on every keystroke) doesn't re-render every card in the grid —
// only the ones whose own props actually changed.
const MemberCard = memo(function MemberCard({
  client,
  currentPackage,
  status,
  validUntil,
  usedSessions,
  totalSessions,
  onEdit,
  onDelete,
  onViewPackage,
}) {
  const fullName =
    `${client.first_name || ""} ${client.last_name || ""}`.trim() ||
    "Bez imena";
  const initials =
    `${(client.first_name || "")[0] || ""}${(client.last_name || "")[0] || ""}`.toUpperCase();

  return (
    <div className="hf-mem-card">
      <div className="hf-mem-card-top">
        <div
          className="hf-mem-avatar"
          style={client.photo_url ? undefined : { background: colorForName(fullName) }}
        >
          {client.photo_url ? (
            <img src={client.photo_url} alt="" className="hf-mem-avatar-img" />
          ) : (
            initials || "?"
          )}
        </div>
        <div className="hf-mem-card-identity">
          <div className="hf-mem-name">{fullName}</div>
          {client.email && (
            <div className="hf-mem-contact-row">
              <EmailOutlinedIcon fontSize="inherit" />
              <span>{client.email}</span>
            </div>
          )}
          {client.phone && (
            <div className="hf-mem-contact-row">
              <LocalPhoneOutlinedIcon fontSize="inherit" />
              <span>{client.phone}</span>
            </div>
          )}
        </div>
      </div>

      <div className="hf-mem-card-meta">
        {currentPackage ? (
          <button
            type="button"
            className={`hf-mem-pkg-btn${
              status === "active" ? "" : " hf-mem-pkg-btn-expired"
            }`}
            onClick={() => onViewPackage(client)}
          >
            <div className="hf-mem-pkg-btn-top">
              <span className="hf-mem-pkg-btn-name">
                {displayPackageName(currentPackage.package_name)}
              </span>
              <span className="hf-mem-pkg-btn-count">
                {usedSessions}/{totalSessions}
              </span>
            </div>
            <div className="hf-mem-pkg-mini-track">
              <div
                className="hf-mem-pkg-mini-fill"
                style={{
                  width: `${
                    totalSessions > 0
                      ? Math.min(100, Math.round((usedSessions / totalSessions) * 100))
                      : 0
                  }%`,
                }}
              />
            </div>
            <span className="hf-mem-pkg-btn-valid">
              {status === "active" ? "Važi do" : "Isteklo"}{" "}
              {validUntil.format("DD.MM.YYYY")}
            </span>
          </button>
        ) : (
          // No package at all yet — still routes into the same popup (not straight into
          // PackageAssignFlow) so the "No Package" placeholder card + Add Package button
          // inside PackageDetailsModal are reachable, per the redesign spec.
          <button
            type="button"
            className="hf-mem-badge hf-mem-badge-none hf-mem-badge-clickable"
            onClick={() => onViewPackage(client)}
          >
            <AddIcon sx={{ fontSize: 13 }} />
            Dodeli paket
          </button>
        )}
      </div>

      <div className="hf-mem-card-actions">
        <button
          type="button"
          className="hf-mem-action-btn"
          onClick={() => onEdit(client)}
        >
          <EditIcon fontSize="small" />
          Izmeni
        </button>
        <button
          type="button"
          className="hf-mem-action-btn hf-mem-action-danger"
          onClick={() => onDelete(client)}
        >
          <DeleteOutlineIcon fontSize="small" />
          Obriši člana
        </button>
      </div>
    </div>
  );
});

// Compact skeleton matching the real card's layout (avatar + two text
// lines + badge row + action row) so the page shell/search/header stay put
// and the list "fills in" instead of a blocking spinner replacing them.
const MemberCardSkeleton = () => (
  <div className="hf-mem-card hf-mem-card-skeleton" aria-hidden="true">
    <div className="hf-mem-card-top">
      <div className="hf-mem-skel hf-mem-skel-avatar" />
      <div className="hf-mem-card-identity">
        <div className="hf-mem-skel hf-mem-skel-line" style={{ width: "55%" }} />
        <div className="hf-mem-skel hf-mem-skel-line" style={{ width: "80%" }} />
      </div>
    </div>
    <div className="hf-mem-card-meta">
      <div className="hf-mem-skel hf-mem-skel-badge" />
    </div>
    <div className="hf-mem-card-actions">
      <div className="hf-mem-skel hf-mem-skel-btn" />
      <div className="hf-mem-skel hf-mem-skel-btn" />
    </div>
  </div>
);

const Members = () => {
  const { showAlert } = useAlert();
  const {
    clients,
    packagesByClient,
    loading,
    error: loadError,
  } = useMembers();
  const [search, setSearch] = useState("");
  // Default: Ime (First Name), A → Š — see the spec this was built against for why first name
  // (not last name) is the primary sort out of the box.
  const [sortField, setSortField] = useState("first_name");
  const [sortDir, setSortDir] = useState("asc");
  const [showPopup, setShowPopup] = useState(false);
  const [activeClientInformation, setActiveClientInformation] =
    useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [packageAssignTarget, setPackageAssignTarget] = useState(null);
  const [packageDetailsTarget, setPackageDetailsTarget] = useState(null);
  const [packageEditTarget, setPackageEditTarget] = useState(null);

  // Add flow: PopUpTest passes the newly created client straight through so
  // it can be added to the shared cache without refetching the whole list.
  // If an older deployed backend hasn't started returning it yet, fall back
  // to a real refresh so the new member still shows up either way.
  const closePopup = (newClient) => {
    setShowPopup(false);
    if (newClient) {
      upsertClientInCache(newClient);
    } else {
      refreshMembers();
    }
  };

  // X / Back in the popup, before anything was saved — just close, no cache update or
  // refetch (that only needs to happen after an actual save, via `closePopup` above through
  // `openPopup`). Same fix already applied to the New Appointment flow's own nested Add
  // Member popup, for the same reason: closing without saving shouldn't trigger a network
  // refetch of the whole member list.
  const closeAddMemberCancel = () => setShowPopup(false);

  // Edit flow: PopUpTest passes the merged updated client via `refresh`.
  const handleMemberEdited = (updatedClient) => {
    if (updatedClient) upsertClientInCache(updatedClient);
  };

  const openAddMember = () => {
    setActiveClientInformation(null);
    setShowPopup(true);
  };

  const openEditMember = useCallback((client) => {
    setActiveClientInformation(client);
    setShowPopup(true);
  }, []);

  const openDeleteMember = useCallback((client) => setDeleteTarget(client), []);

  const openPackageDetails = useCallback(
    (client) => setPackageDetailsTarget(client),
    []
  );

  const handleConfirmDeleteMember = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const google_token = localStorage.getItem("googleAccessToken");
      await ApiMethods.post(`auth/delete_client/${deleteTarget.id}`, {
        googleToken: google_token,
      });
      showAlert("Član je obrisan", "success");
      removeClientFromCache(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      console.log(err);
      showAlert("Greška pri brisanju člana", "error");
    } finally {
      setDeleting(false);
    }
  };

  const memberRows = useMemo(() => {
    return clients.map((client) => {
      const packages = packagesByClient[client.id] || [];
      const sortedPackages = [...packages].sort((a, b) =>
        moment(b.start_date).diff(moment(a.start_date))
      );
      const currentPackage = sortedPackages[0];
      let status = "none";
      let validUntil = null;
      let usedSessions = 0;
      let totalSessions = 0;
      if (currentPackage) {
        // `end_date` is the authoritative, server-computed value (added
        // alongside the package-assignment flow). Older rows created
        // before that existed fall back to the previous day-count
        // estimate so they still render something reasonable.
        validUntil = currentPackage.end_date
          ? moment(currentPackage.end_date)
          : moment(currentPackage.start_date).add(
              currentPackage.package_period_days || 0,
              "days"
            );
        totalSessions =
          currentPackage.total_sessions ?? currentPackage.package_service_qty ?? 0;
        usedSessions = Math.min(
          currentPackage.used ?? 0,
          totalSessions || Number.MAX_SAFE_INTEGER
        );
        // No `totalSessions > 0` guard: a 0-qty package (e.g. the legacy-backfill
        // placeholder) has 0 used >= 0 total immediately, so it reads as used-up
        // (inactive) rather than perpetually "active".
        const isUsedUp = usedSessions >= totalSessions;
        const isPastEnd = validUntil.isBefore(moment(), "day");
        status = isUsedUp || isPastEnd ? "inactive" : "active";
      }
      return {
        client,
        currentPackage,
        allPackages: sortedPackages,
        status,
        validUntil,
        usedSessions,
        totalSessions,
      };
    });
  }, [clients, packagesByClient]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return memberRows;
    return memberRows.filter(({ client }) => {
      const haystack = `${client.first_name || ""} ${client.last_name || ""} ${
        client.email || ""
      } ${client.phone || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [memberRows, search]);

  // Search always filters first, then the currently selected sort is applied on top — changing
  // sort never resets or re-runs the search, and vice versa (two independent useMemos over the
  // same pipeline). Last Name is the secondary sort for First Name (and vice versa) so members
  // sharing a first name land in a stable, still-alphabetical order rather than whatever the
  // underlying array's original order happened to be; Date Added has no secondary sort of its
  // own since timestamps are effectively unique.
  const sortedRows = useMemo(() => {
    const compare = compareMembersBy(sortField, sortDir);
    return [...filteredRows].sort((rowA, rowB) => compare(rowA.client, rowB.client));
  }, [filteredRows, sortField, sortDir]);

  // Cold load (no cached data yet) vs. a silent background refresh of
  // already-visible data — only the former blocks the grid with skeletons;
  // the latter keeps showing what's cached while it updates underneath.
  const showSkeleton = loading && clients.length === 0;
  const showBlockingError = !loading && loadError && clients.length === 0;
  const showBackgroundErrorBanner = loadError && clients.length > 0;

  return (
    <AppShell active="members">
      {/* Member count now folds into the title itself ("Članovi (266)") instead of a separate
          subtitle line underneath — saves a row of height on Mobile. Loading/error states just
          show the plain title with no count, same as before there was one to show. */}
      <MobileHeader
        title={
          showSkeleton || showBlockingError
            ? "Članovi"
            : `Članovi (${clients.length})`
        }
      />

      <button
        type="button"
        className="hf-mem-mobile-fab"
        onClick={openAddMember}
        aria-label="Novi član"
      >
        <PersonAddAlt1Icon />
      </button>

      <div className="hf-mem-page">
        <div className="hf-mem-header">
          <div>
            <h1 className="hf-mem-title">Članovi</h1>
            <p className="hf-mem-subtitle">
              {showSkeleton
                ? "Učitavanje..."
                : showBlockingError
                ? "Greška pri učitavanju"
                : `${clients.length} članova`}
            </p>
          </div>
          <button
            type="button"
            className="hf-mem-add-btn"
            onClick={openAddMember}
          >
            <PersonAddAlt1Icon fontSize="small" />
            Novi član
          </button>
        </div>

        <div className="hf-mem-search-wrap">
          <SearchIcon fontSize="small" className="hf-mem-search-icon" />
          <input
            className="hf-mem-search-input"
            type="text"
            placeholder="Pretraži po imenu, email-u ili telefonu"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <MembersSortControl
            field={sortField}
            dir={sortDir}
            onSelect={(nextField, nextDir) => {
              setSortField(nextField);
              setSortDir(nextDir);
            }}
          />
        </div>

        {showBackgroundErrorBanner && (
          <div className="hf-mem-error-banner">
            <span>
              Osvežavanje liste nije uspelo — prikazani su poslednje učitani
              podaci.
            </span>
            <button type="button" onClick={() => refreshMembers()}>
              Pokušaj ponovo
            </button>
          </div>
        )}

        {showBlockingError && (
          <div className="hf-mem-empty">
            <p>Nije moguće učitati listu članova.</p>
            <button
              type="button"
              className="hf-mem-retry-btn"
              onClick={() => refreshMembers()}
            >
              Pokušaj ponovo
            </button>
          </div>
        )}

        {!showSkeleton && !showBlockingError && sortedRows.length === 0 && (
          <div className="hf-mem-empty">
            <p>
              {search
                ? "Nema rezultata za pretragu."
                : "Nema pronađenih članova."}
            </p>
          </div>
        )}

        {showSkeleton ? (
          <div className="hf-mem-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <MemberCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          !showBlockingError && (
            <div className="hf-mem-grid">
              {sortedRows.map(
                ({
                  client,
                  currentPackage,
                  status,
                  validUntil,
                  usedSessions,
                  totalSessions,
                }) => (
                  <MemberCard
                    key={client.id}
                    client={client}
                    currentPackage={currentPackage}
                    status={status}
                    validUntil={validUntil}
                    usedSessions={usedSessions}
                    totalSessions={totalSessions}
                    onEdit={openEditMember}
                    onDelete={openDeleteMember}
                    onViewPackage={openPackageDetails}
                  />
                )
              )}
            </div>
          )
        )}
      </div>

      {showPopup && (
        <PopupTest
          setActiveClientInformation={setActiveClientInformation}
          activeClientInformation={activeClientInformation}
          refresh={handleMemberEdited}
          openPopup={closePopup}
          onCancel={closeAddMemberCancel}
          onBack={activeClientInformation ? undefined : closeAddMemberCancel}
        />
      )}

      {deleteTarget && (
        <DeletePopUp
          message={`Da li ste sigurni da želite da obrišete člana ${deleteTarget.first_name || ""} ${deleteTarget.last_name || ""}?`}
          onConfirm={handleConfirmDeleteMember}
          closePopUp={() => setDeleteTarget(null)}
        />
      )}

      {packageAssignTarget && (
        <PackageAssignFlow
          open
          member={packageAssignTarget}
          onClose={() => setPackageAssignTarget(null)}
          onAssigned={() => setPackageAssignTarget(null)}
        />
      )}

      {packageDetailsTarget && (
        <PackageDetailsModal
          open
          member={packageDetailsTarget}
          packages={packagesByClient[packageDetailsTarget.id] || []}
          onClose={() => setPackageDetailsTarget(null)}
          onAssignNew={() => {
            const target = packageDetailsTarget;
            setPackageDetailsTarget(null);
            setPackageAssignTarget(target);
          }}
          onChangePackage={(currentPackage) => {
            const target = packageDetailsTarget;
            setPackageDetailsTarget(null);
            setPackageEditTarget({ client: target, editingPackage: currentPackage });
          }}
        />
      )}

      {packageEditTarget && (
        <ClientPackageDialog
          open
          clientId={
            packageEditTarget.client.id != null
              ? packageEditTarget.client.id
              : packageEditTarget.client.client_id
          }
          editingPackage={packageEditTarget.editingPackage}
          onClose={() => setPackageEditTarget(null)}
          onSaved={(updated) => {
            const clientId =
              packageEditTarget.client.id != null
                ? packageEditTarget.client.id
                : packageEditTarget.client.client_id;
            if (updated) upsertClientPackageInCache(clientId, updated);
            setPackageEditTarget(null);
          }}
        />
      )}
    </AppShell>
  );
};

export default Members;
